import { NavigationEnd, Router } from "@angular/router";
import { filter, firstValueFrom } from "rxjs";

// Set by scripts/prerender.js on a pre-rendered snapshot: the rendered page sits in #prerender-snapshot next to an
// empty app-root, which index.html keeps hidden while it carries this attribute.
export const SNAPSHOT_ID = "prerender-snapshot";
export const PENDING_ATTRIBUTE = "data-prerender-pending";

// The app counts as caught up once it shows (nearly) as much text as the snapshot did, on consecutive checks - so a
// page that is still filling in isn't swapped in halfway. ApplicationRef.whenStable() can't be used for this: with
// zone.js it never resolves on pages with recurring timers.
const TEXT_RATIO = 0.9;
const STABLE_CHECKS = 2;
const POLL_INTERVAL_MS = 100;
// Never leave the visitor on the static snapshot longer than this, even if the page renders differently
const MAX_WAIT_MS = 5000;
// Room for the sticky AnchorNavigation above an anchor, the same offset its own scrollTo() uses
const ANCHOR_OFFSET = 120;

/**
 * Swaps a pre-rendered snapshot for the live app in one go, once the app has rendered the same page in the hidden
 * app-root. Without this, Angular would wipe the snapshot on bootstrap and the page would flash empty while the
 * route, translations and data load. Does nothing for pages that weren't served as a snapshot.
 */
export async function replacePrerenderedSnapshot(dom: Document, router: Router): Promise<void> {
  const snapshot = dom.getElementById(SNAPSHOT_ID);
  const appRoot = dom.querySelector("app-root");
  if (!snapshot || !appRoot?.hasAttribute(PENDING_ATTRIBUTE)) {
    return;
  }

  const deadline = Date.now() + MAX_WAIT_MS;
  const expectedTextLength = textLength(snapshot);

  // Until the swap, nothing in the snapshot has a click handler. Its in-page links (AnchorNavigation) point to the path
  // the page was pre-rendered at, without the query string a visitor may have come in with (?store-from=...), so
  // following them would reload the page without it (COMBI-4577). Scroll to the anchor in the snapshot instead.
  snapshot.addEventListener("click", event => scrollToSnapshotAnchor(event, snapshot, dom));

  try {
    await withDeadline(firstValueFrom(router.events.pipe(filter(e => e instanceof NavigationEnd))), deadline);
    let previousLength = -1;
    let stableChecks = 0;
    while (Date.now() < deadline) {
      const length = textLength(appRoot);
      stableChecks = length >= expectedTextLength * TEXT_RATIO && length === previousLength ? stableChecks + 1 : 0;
      if (stableChecks >= STABLE_CHECKS) {
        break;
      }
      previousLength = length;
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  } finally {
    // the live page has the same layout, so keep the visitor where they scrolled to on the snapshot
    const scrollY = window.scrollY;
    snapshot.remove();
    appRoot.removeAttribute(PENDING_ATTRIBUTE);
    window.scrollTo(0, scrollY);
  }
}

// textContent rather than innerText: the app-root is still hidden, and innerText would ignore display:none content
function textLength(element: Element): number {
  return (element.textContent || "").replace(/\s+/g, " ").trim().length;
}

/**
 * Handles a click on a link in the snapshot to an anchor on this same page: scrolls to that anchor in the snapshot
 * instead of letting the browser navigate. Other clicks (other pages, modifier keys, unknown anchors) are left alone.
 */
export function scrollToSnapshotAnchor(event: MouseEvent, snapshot: Element, dom: Document): void {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  const link = (event.target as Element | null)?.closest("a[href]");
  if (!(link instanceof HTMLAnchorElement) || !link.hash
      || link.origin !== dom.location.origin || link.pathname !== dom.location.pathname) {
    return;
  }
  // look inside the snapshot: the hidden app-root may already carry an element with the same id
  const anchor = snapshot.querySelector("#" + CSS.escape(decodeURIComponent(link.hash.substring(1))));
  const view = dom.defaultView;
  if (!anchor || !view) {
    return;
  }
  event.preventDefault();
  // no smooth scrolling: the swap keeps the scroll position, and would interrupt an animation halfway
  view.scrollTo(0, anchor.getBoundingClientRect().top + view.scrollY - ANCHOR_OFFSET);
}

function withDeadline<T>(promise: Promise<T>, deadline: number): Promise<T | void> {
  return Promise.race([promise, new Promise<void>(resolve => setTimeout(resolve, Math.max(0, deadline - Date.now())))]);
}
